import {z} from 'zod';

export type AddressV2 = {
  street: string;
  descriptiveNumber: string | null;
  orientationNumber: string | null;
  city: string;
  zip: string;
  addressComplement: string | null;
  country: string;
  prefix: string | null;
  district: string | null;
  state: string | null;
  coordinates?: {
    latitude: string;
    longitude: string;
  } | null;
  postal?: string | null;
  externalAddressId?: string | null;
};

export type PutAddressV2 = Omit<AddressV2, 'externalAddressId'>;

type PhoneNumberV2 = {
  countryCode: string;
  prefix: string;
  number: string;
};

type SelfEmployedInformation = {
  titleBefore: string | null;
  firstName: string;
  lastName: string;
  titleAfter: string | null;
  phoneNumber: PhoneNumberV2 | null;
  emailAddress: string | null;
};

type ContactInformation<TAddress = AddressV2> = {
  registrationNumber: string;
  vatNumber: string | null;
  companyName: string;
  fileNumber: string | null;
  address: TAddress;
  selfEmployedInformation: SelfEmployedInformation | null;
};

type PublicContacts = {
  phoneNumber: PhoneNumberV2 | null;
  emailAddress: string | null;
  website: string | null;
};

type ContactPerson = {
  firstName: string | null;
  lastName: string | null;
  role: string | null;
  isPrimary: boolean | null;
  phoneNumber: PhoneNumberV2 | null;
  emailAddress: string | null;
};

export type PutTenantV2ApiReq = {
  type: string;
  contactInformation: ContactInformation<PutAddressV2>;
  publicContacts: PublicContacts;
  contactPersons: ContactPerson[];
};

export type PutBillingInformationV2ApiArg = {
  id: string;
  billingName: string | null;
  contactInformation: ContactInformation<PutAddressV2>;
  deputyPersons: DeputyPersonV2[];
  bankAccounts: BankAccount[];
};

export type GetBillingInformationV2ApiRes = {
  id: string;
  billingName: string | null;
  isPrimary: boolean;
  contactInformation: ContactInformation;
  deputyPersons: DeputyPersonV2[];
  bankAccounts: BankAccount[];
};

export type GetBillingInformationListV2ApiRes = GetBillingInformationV2ApiRes[];

export type GetBillingInformationV2ApiArg = {
  id: string;
};

export type PostBillingInformationV2ApiArg = {
  billingName: string | null;
  contactInformation: ContactInformation<PutAddressV2>;
  deputyPersons: DeputyPersonV2[];
  bankAccounts: BankAccount[];
};

export type GetTenantV2ApiRes = {
  id: string;
  country: string;
  currency: string;
  type: string;
  contactInformation: ContactInformation;
  publicContacts: PublicContacts;
  contactPersons: ContactPerson[];
  billingInformation: BillingInformationV2[];
};

export type BillingInformationV2 = {
  id: string;
  billingName: string;
  isPrimary: boolean;
  contactInformation: ContactInformation;
  deputyPersons: DeputyPersonV2[];
  bankAccounts: BankAccount[];
};

export type PersonalIdType =
  | 'ID_TYPE_ID'
  | 'ID_TYPE_PASSPORT'
  | 'ID_TYPE_DRIVING_LICENSE'
  | 'ID_TYPE_OTHER';
export type DeputyPersonV2 = {
  isPrimary: boolean;
  firstName: string;
  lastName: string | null;
  dateOfBirth: string | null;
  identificationNumber: string | null;
  personalIdType: PersonalIdType | null;
  personalIdNumber: string | null;
  validUntil: string | null;
  issuedBy: string | null;
};

type BankAccount = {
  accountName: string;
  accountNumber: string;
  bankName: string | null;
  isPrimary: boolean | null;
  currency: string | null;
  swift: string | null;
  iban: string | null;
};

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;

export const TimeRangeSchema = z.object({
  from: z.string().regex(TIME_PATTERN),
  to: z.string().regex(TIME_PATTERN),
});

export type TimeRange = z.infer<typeof TimeRangeSchema>;

export const OpeningHoursSchema = z.object({
  monday: z.array(TimeRangeSchema).optional(),
  tuesday: z.array(TimeRangeSchema).optional(),
  wednesday: z.array(TimeRangeSchema).optional(),
  thursday: z.array(TimeRangeSchema).optional(),
  friday: z.array(TimeRangeSchema).optional(),
  saturday: z.array(TimeRangeSchema).optional(),
  sunday: z.array(TimeRangeSchema).optional(),
});

export type OpeningHoursRequestBody = z.infer<typeof OpeningHoursSchema>;
export type OpeningHoursResponseBody = z.infer<typeof OpeningHoursSchema>;

export const BulkUpdateOpeningHoursRequestBodySchema = z.object({
  branchIds: z.array(z.string().uuid()).min(1),
  salesOpeningHours: OpeningHoursSchema.nullable().optional(),
  workshopOpeningHours: OpeningHoursSchema.nullable().optional(),
  rentalOpeningHours: OpeningHoursSchema.nullable().optional(),
});

export type BulkUpdateOpeningHoursRequestBody = z.infer<
  typeof BulkUpdateOpeningHoursRequestBodySchema
>;

export type PutBranchV2ApiArg = {
  id: string;
  name: string;
  billingInformationId: string;
  address: PutAddressV2;
  publicContacts: PublicContacts | null;
  contactPersons: ContactPerson[] | null;
  accountingExternalId: string | null;
  salesOpeningHours?: OpeningHoursRequestBody | null;
  workshopOpeningHours?: OpeningHoursRequestBody | null;
  rentalOpeningHours?: OpeningHoursRequestBody | null;
};

export type PostBranchV2ApiArg = {
  name: string;
  billingInformationId: string;
  address: PutAddressV2;
  publicContacts: PublicContacts | null;
  contactPersons: ContactPerson[] | null;
  accountingExternalId: string | null;
  salesOpeningHours?: OpeningHoursRequestBody | null;
  workshopOpeningHours?: OpeningHoursRequestBody | null;
  rentalOpeningHours?: OpeningHoursRequestBody | null;
};

export type GetEmployeeByUserIdApiRes = {
  id: string;
  firstName: string;
  lastName: string;
  note: string;
  photo: {
    fileId: string;
    filename: string;
    uri: string;
  };
};

export type GetEmployeeByUserIdApiArg = {
  userId: string;
};

export type GetBranchV2ApiRes = {
  id: string;
  tenantId: string;
  name: string;
  code: string | null;
  registrationNumber: string;
  fileNumber: string;
  vatNumber: string;
  companyName: string;
  address: AddressV2 | null;
  publicContacts: PublicContacts | null;
  contactPersons: ContactPerson[];
  billingInformation: BillingInformationV2 | null;
  correspondingAddresses: {id: string; address: AddressV2}[] | null;
  accountingExternalId: string | null;
  salesOpeningHours: OpeningHoursResponseBody | null;
  workshopOpeningHours: OpeningHoursResponseBody | null;
  rentalOpeningHours: OpeningHoursResponseBody | null;
};

export type GetBranchV2ApiArg = {
  id: string;
};

export type PutBranchV2ApiRes = void;
export type PostBranchV2ApiRes = {id: string};

export type MultiFactorAuthenticationLoginType = 'totp';

export type ActivateMultiFactorAuthenticationApiArg = {
  userGracePeriodInDays: number;
  availableLoginTypes: MultiFactorAuthenticationLoginType[];
};

export type LoginUserMultiFactorAuthenticationApiRes = {
  preferredLoginType: MultiFactorAuthenticationLoginType | null;
  activeLoginTypes: MultiFactorAuthenticationLoginType[];
};

export type MultiFactorAuthenticationSettingsApiRes = {
  active: boolean;
  userGracePeriodInDays: number | null;
  availableLoginTypes: MultiFactorAuthenticationLoginType[] | null;
};

export type MultiFactorAuthenticationLoginTypeOperation = 'activate' | 'deactivate';

export type UserMultiFactorAuthenticationHistoryResponseBody = {
  operation: MultiFactorAuthenticationLoginTypeOperation;
  type: MultiFactorAuthenticationLoginType;
  time: string;
};

export type MultiFactorAuthenticationTotpGenerateApiRes = {
  barcode: string;
  url: string;
  secret: string;
};

export type MultiFactorAuthenticationTotpGenerateApiArg = void;

export type MultiFactorAuthenticationTotpVerifyApiRes = void;

export type MultiFactorAuthenticationTotpDeactivateApiRes = void;

export type MultiFactorAuthenticationTotpDeactivateApiArg = {
  userId: string;
};

export type MultiFactorAuthenticationTotpResetApiArg = {
  userId: string;
};

export type MultiFactorAuthenticationTotpResetApiRes = void;

export type MultiFactorAuthenticationTotpVerifyApiArg = {
  code: string;
};

export type UserMultiFactorAuthenticationApiRes = {
  setUpAt: string;
  activateUntil: string;
  activatedAt: string | null;
  deactivatedAt: string | null;
  preferredLoginType: MultiFactorAuthenticationLoginType | null;
  availableLoginTypes: MultiFactorAuthenticationLoginType[];
  activeLoginTypes: MultiFactorAuthenticationLoginType[];
  history: UserMultiFactorAuthenticationHistoryResponseBody[];
};

export type PostOAuthTokenApiArg = {
  grant_type: string;
  code: string;
  redirect_uri: string;
  client_id: string;
  workspace?: string;
};

export type PostOAuthTokenApiRes = {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
};
